import { HOUR } from '../constants/expiry'
import type { ShareFormat } from '../types/share'
import { getExtension, getExtensionForFormat } from './filename'

/**
 * Get the URL extension for a share, resolving from filename first, then format.
 * Defaults to ".md".
 */
export function getShareExtension(filename?: string | null, format?: ShareFormat): string {
  if (filename) {
    const ext = getExtension(filename)
    if (ext) return ext
  }
  if (format) return getExtensionForFormat(format)
  return '.md'
}

/**
 * Build a relative share URL (e.g. /view/abc123.md#passcode)
 */
export function buildShareUrl(shareId: string, extension: string, passcode?: string | null, mode?: string): string {
  const base = mode
    ? `/view/${shareId}${extension}?mode=${mode}`
    : `/view/${shareId}${extension}`
  return passcode ? `${base}#${passcode}` : base
}

/**
 * Extract the passcode from a share URL's hash fragment.
 */
export function extractPasscodeFromUrl(url: string): string | null {
  const hashIndex = url.indexOf('#')
  if (hashIndex === -1) return null
  return url.slice(hashIndex + 1) || null
}

/**
 * Check if a share is expiring within 24 hours
 */
export function isExpiringSoon(expiresAt: number): boolean {
  const hoursRemaining = (expiresAt - Date.now()) / HOUR
  return hoursRemaining > 0 && hoursRemaining < 24
}

/**
 * Get expiry progress as a 0-100 percentage (for the last 24 hours)
 */
export function getExpiryProgress(expiresAt: number): number {
  const hoursRemaining = (expiresAt - Date.now()) / HOUR
  return Math.max(0, Math.min(100, (hoursRemaining / 24) * 100))
}

/**
 * Get the color scheme for an expiry progress indicator.
 * Below 25% remaining → 'red' (critical), otherwise → 'orange' (warning).
 */
export function getExpiryColorScheme(progress: number): 'red.400' | 'orange.400' {
  return progress < 25 ? 'red.400' : 'orange.400'
}
